/**
 * Una conversación de Intercom por episodio de ayuda: si la cocina vuelve a escalar poco después, el
 * mensaje nuevo va a la conversación que ya abrió la tablet, en vez de abrir otra en la bandeja.
 *
 * Por qué hace falta: `Intercom('startConversation', texto)` crea SIEMPRE una conversación nueva, y
 * el SDK no dice el id de la que acaba de crear (ni método ni evento; confirmado en su documentación
 * y en su comunidad). Lo que sí tiene es `showConversation(id)`, que abre una concreta. El id sale de
 * lo que el propio messenger guarda de sí mismo en el localStorage de la página, que es el nuestro
 * porque su iframe es del mismo origen. Escribir en esa conversación es de intercomComposer.js.
 *
 * Medido sobre el widget real el 30-sep-2026 (Messenger con Fin, workspace de Bronze):
 *  - la clave es `intercom.intercom-state-<app_id>`, un JSON cuyo `router.location.pathname` vale
 *    `/messages/conversation/<id>` mientras el messenger enseña esa conversación, y cuyo
 *    `conversations.byId` lista las que ha cargado desde que arrancó la página;
 *  - tras `startConversation`, la ruta pasa a la conversación nueva en cuanto el servidor la crea;
 *  - al recargar, el messenger arranca con ese estado VACÍO: no recuerda nada entre recargas, así que
 *    la app guarda el id en su propia clave (y así sobrevive al reinicio que propone la Ayuda);
 *  - `showConversation` con un id que ya no vale abre una vista vacía, sin redactor (y el servidor
 *    responde 404): ahí la app se rinde y abre una conversación nueva, como antes.
 *
 * Nada de esto es un contrato de Intercom. Si cambian la forma de ese estado, la app deja de
 * encontrar el id y se comporta como antes de esto: una conversación nueva por escalado. Nunca lanza.
 */

// Workspace de la región UE (oct-2026). El SDK elige la API por `region` ('eu' → api-iam.eu.intercom.io)
// y pisa cualquier `api_base` que se le pase: sin esto hablaría con la de EE. UU.
export const INTERCOM_APP_ID = 'm66948ae';
export const INTERCOM_REGION = 'eu';
export const HELP_CONVERSATION_KEY = 'bronze_help_conversation';

/**
 * Cuánto dura un episodio: la tablet sigue en la misma conversación mientras el último mensaje que
 * mandó ahí tenga menos de una hora. No es un número elegido a ojo. En septiembre de 2026, contando
 * solo cocinas reales (sin las tablets de prueba), hubo 42 escalados repetidos: 29 de ellos (el 69 %)
 * llegaron a menos de una hora del anterior, y 21 de esos 29 eran del mismo tema. Pasada la hora, casi
 * todo es otro día (11 de los 13 restantes, a más de 12 h), que es otro problema y otra conversación.
 */
export const CONTINUE_WINDOW_MS = 60 * 60 * 1000;

const CONVERSATION_PATH = /\/conversations?\/(\d+)(?:[/?#]|$)/;
const CONVERSATION_ID = /^\d+$/;

/**
 * Lo que el messenger dice de sí mismo: la conversación que enseña ahora (o null) y las que conoce.
 * null si no hay estado o no se entiende.
 *
 * @returns {{ shown: string|null, known: string[] } | null}
 */
export function messengerConversations(storage, appId = INTERCOM_APP_ID) {
  try {
    const raw = storage && storage.getItem(`intercom.intercom-state-${appId}`);
    if (!raw) return null;
    const state = JSON.parse(raw);
    if (!state || typeof state !== 'object') return null;
    const path = state.router && state.router.location && state.router.location.pathname;
    const match = typeof path === 'string' ? path.match(CONVERSATION_PATH) : null;
    const byId = state.conversations && state.conversations.byId;
    const known = byId && typeof byId === 'object' ? Object.keys(byId).filter((id) => CONVERSATION_ID.test(id)) : [];
    return { shown: match ? match[1] : null, known };
  } catch (_) {
    return null;
  }
}

/** Los ids que ya existían: lo que el messenger conoce y lo que enseña, para no confundirlos con uno nuevo. */
export function conversationsBefore(storage, appId = INTERCOM_APP_ID) {
  const state = messengerConversations(storage, appId);
  if (!state) return [];
  return state.shown ? [...new Set([...state.known, state.shown])] : state.known;
}

/**
 * La conversación en la que seguir, si la hay: la última que abrió ESTA tablet (el mismo `user`, que
 * es el deviceId con el que se identifica en Intercom) y en la que mandó algo hace menos de
 * `windowMs`. Un reloj que ha ido hacia atrás no cuenta como dentro del episodio.
 *
 * @returns {string|null}
 */
export function conversationToContinue(storage, { user, now = Date.now(), windowMs = CONTINUE_WINDOW_MS } = {}) {
  try {
    const raw = storage && storage.getItem(HELP_CONVERSATION_KEY);
    if (!raw || !user) return null;
    const record = JSON.parse(raw);
    if (!record || typeof record.id !== 'string' || !CONVERSATION_ID.test(record.id)) return null;
    if (record.user !== user) return null;
    const age = now - Number(record.at);
    if (!Number.isFinite(age) || age < 0 || age > windowMs) return null;
    return record.id;
  } catch (_) {
    return null;
  }
}

/** Apunta la conversación del episodio; cada mensaje que va a ella la renueva. */
export function rememberConversation(storage, { id, user, now = Date.now() } = {}) {
  if (!storage || !user || !CONVERSATION_ID.test(String(id || ''))) return;
  try {
    storage.setItem(HELP_CONVERSATION_KEY, JSON.stringify({ id: String(id), user, at: now }));
  } catch (_) { /* sin almacenamiento, el siguiente escalado abre otra, como antes */ }
}

export function forgetConversation(storage) {
  try {
    if (storage) storage.removeItem(HELP_CONVERSATION_KEY);
  } catch (_) { /* nada que olvidar */ }
}

/**
 * Espera a que el messenger enseñe una conversación que no estaba en `before` y que `accept` reconozca
 * como la nuestra: la que acaba de crear `startConversation` (o la persona, al pulsar enviar en un
 * redactor pre-rellenado). Que sea nueva no basta, porque en ese rato la persona puede abrir otra
 * desde el propio chat (una antigua, o la de un mensaje de soporte que acaba de llegar), y esa no es
 * la del episodio. Sondeo acotado: se rinde a los `timeoutMs`, o antes si `stillLooking()` dice que
 * ya no tiene sentido esperar.
 *
 * @returns {Promise<string|null>} el id, o null si no apareció
 */
export function waitForNewConversation({
  storage,
  appId = INTERCOM_APP_ID,
  before = [],
  accept = () => true,
  timeoutMs = 20000,
  intervalMs = 250,
  stillLooking = () => true,
} = {}) {
  const old = new Set(before);
  const startedAt = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      try {
        const state = messengerConversations(storage, appId);
        if (state && state.shown && !old.has(state.shown) && accept(state.shown)) { resolve(state.shown); return; }
        if (Date.now() - startedAt >= timeoutMs || !stillLooking()) { resolve(null); return; }
      } catch (_) {
        resolve(null);
        return;
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}
